"use client"

import * as React from "react"

import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { TelegramIcon, WhatsAppIcon, XTwitterIcon } from "@/components/ui/icons"
import { wheelAudio } from "@/components/wheel/wheel-audio"
import { cardsAudio } from "@/components/cards/cards-audio"
import { CardBack } from "@/components/cards/card-back"
import { CardFace } from "@/components/cards/card-face"
import { CardLamps, type LampProgram } from "@/components/cards/lamps"
import type { CardPack } from "@/lib/cards/nfl-pack"
import type { PlayerCardData } from "@/lib/display/player-card"
import { createSlipPickFromCard } from "@/lib/slip/slip-pick"
import {
  selectAddPick,
  selectClearSlip,
  selectFlashPicks,
  selectHidePicks,
  selectLockPromoLineup,
  selectRevealPicks,
  useSlipStore,
} from "@/lib/store/slip-store"
import { cn } from "@/lib/utils"

/* =============================================================================
   Opening a pack.

   The wheel's choreography on a deck. Press: the room goes dark and the
   whoosh runs. The cards fan off the stack into a hand, full size, face
   down, one after another. Then the beat: nothing moves, the room is dark,
   the riser climbs for two and a half seconds. Then they all turn
   over together, a ripple along the fan, every card throwing sparks, with
   the payout sound. The room lets go and the hand lies there; the card
   under the pointer lifts and its neighbours part. Card is card: no tiers, no tags.
   Transforms and opacity only. Everything is positioned in one stage box.
   ============================================================================= */

interface CardState {
  spot: "stack" | "fan" | "lift" | "slip"
  faceUp: boolean
  /** The walkout, from the turn on: sparks. */
  walkout: boolean
}

const OPEN_HOLD_MS = 900
const DEAL_MS = 1250
const DEAL_STAGGER_MS = 150
const TEASE_MS = 2600
const FLIP_MS = 720
const FLIP_STAGGER_MS = 70
const SETTLE_MS = 1400
/** The flight: up off the fan, then the swoop into the slip. */
const LIFT_MS = 300
const FLY_MS = 780
const FLY_STAGGER_MS = 520
const FLY_SCALE = 0.17
const LAND_SPARKS = 14
const SPARKS = 12
const CARD_W = 300
const STACK_OFFSET = 3
/** The hand: each card this far along and this much turned from the last. */
const FAN_STEP_PX = 150
const FAN_STEP_DEG = 4.5

/* The share: the hand on the screen is the post; this row sends it. Same
   channels as the wheel's card. */
const SHARE_URL = "https://go.getpicks.app/cards"

function ShareCircle({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex size-11 items-center justify-center rounded-full bg-grey-800 text-white transition-colors hover:bg-grey-700"
    >
      {children}
    </button>
  )
}

function ShareRow({ pack }: { pack: CardPack }) {
  const text = `I just opened the ${pack.league} pack on Picks. ${pack.cards.length} cards.`
  function openChannel(channel: "x" | "telegram" | "whatsapp") {
    const links: Record<typeof channel, string> = {
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(SHARE_URL)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(SHARE_URL)}&text=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${SHARE_URL}`)}`,
    }
    window.open(links[channel], "_blank", "noopener,noreferrer")
  }
  function copy() {
    navigator.clipboard.writeText(`${text} ${SHARE_URL}`).then(
      () => toast.success("Link copied"),
      () => toast.error("Could not copy")
    )
  }
  return (
    <div className="flex items-center gap-2">
      <ShareCircle label="X" onClick={() => openChannel("x")}>
        <XTwitterIcon className="size-4" />
      </ShareCircle>
      <ShareCircle label="Telegram" onClick={() => openChannel("telegram")}>
        <TelegramIcon className="size-[18px]" />
      </ShareCircle>
      <ShareCircle label="WhatsApp" onClick={() => openChannel("whatsapp")}>
        <WhatsAppIcon className="size-[18px]" />
      </ShareCircle>
      <Button type="button" variant="outline" onClick={copy} className="ml-1 h-11 rounded-full px-5">
        Copy link
      </Button>
    </div>
  )
}

/* Opening is a swipe: the knob is dragged along the pill, left to right,
   and the pack opens when it reaches the end. Let go early and it slides
   back. The word fades as the knob covers the distance. */
const SLIDE_W = 320
const SLIDE_H = 56
const KNOB = 48
const SLIDE_PAD = (SLIDE_H - KNOB) / 2
const SLIDE_RUN = SLIDE_W - KNOB - SLIDE_PAD * 2

function SlideToOpen({ onOpen }: { onOpen: () => void }) {
  const [x, setX] = React.useState(0)
  const [dragging, setDragging] = React.useState(false)
  const [done, setDone] = React.useState(false)
  const startRef = React.useRef(0)

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (done) return
    e.currentTarget.setPointerCapture(e.pointerId)
    startRef.current = e.clientX - x
    setDragging(true)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragging || done) return
    setX(Math.max(0, Math.min(SLIDE_RUN, e.clientX - startRef.current)))
  }
  const onPointerUp = () => {
    if (!dragging || done) return
    setDragging(false)
    if (x >= SLIDE_RUN * 0.8) {
      setDone(true)
      setX(SLIDE_RUN)
      onOpen()
    } else {
      setX(0)
    }
  }
  const p = x / SLIDE_RUN

  return (
    <div
      role="slider"
      aria-label="Slide to open"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(p * 100)}
      className="relative select-none overflow-hidden rounded-full bg-primary"
      style={{ width: SLIDE_W, height: SLIDE_H, touchAction: "none" }}
    >
      <span
        className="pointer-events-none absolute inset-0 flex items-center justify-center text-base font-semibold text-white"
        style={{ opacity: 1 - p * 1.4, transform: `translateX(${p * 24}px)` }}
      >
        Open
      </span>
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="absolute top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full bg-white text-primary shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
        style={{
          left: SLIDE_PAD,
          width: KNOB,
          height: KNOB,
          transform: `translate(${x}px, -50%)`,
          transition: dragging ? "none" : "transform 420ms cubic-bezier(0.16, 1, 0.3, 1)",
          cursor: done ? "default" : dragging ? "grabbing" : "grab",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </button>
    </div>
  )
}

export interface PackOpeningProps {
  pack: CardPack
  /** The board card behind each face; undefined flies without landing. */
  live?: (PlayerCardData | undefined)[]
  /** The last card has landed and the count has climbed. */
  onApplied?: () => void
  className?: string
}

export function PackOpening({ pack, live, onApplied, className }: PackOpeningProps) {
  const n = pack.cards.length
  const [phase, setPhase] = React.useState<"idle" | "opening" | "done" | "flying" | "applied">("idle")
  const [lamps, setLamps] = React.useState<LampProgram>("off")
  const addPick = useSlipStore(selectAddPick)
  const flashPicks = useSlipStore(selectFlashPicks)
  const hidePicks = useSlipStore(selectHidePicks)
  const revealPicks = useSlipStore(selectRevealPicks)
  const clearSlip = useSlipStore(selectClearSlip)
  const lockPromoLineup = useSlipStore(selectLockPromoLineup)
  /** Each card's landing spot in stage px: the centre of its own row in
      the slip, measured once the pick is in. */
  const [targets, setTargets] = React.useState<Record<number, { x: number; y: number }>>({})
  /** Bursts of sparks where cards have landed. */
  const [bursts, setBursts] = React.useState<{ key: number; x: number; y: number }[]>([])
  const [landed, setLanded] = React.useState(0)
  const fresh = React.useCallback(
    (): CardState[] => pack.cards.map(() => ({ spot: "stack", faceUp: false, walkout: false })),
    [pack]
  )
  const [cards, setCards] = React.useState<CardState[]>(fresh)
  /** The card under the pointer once the hand is open: it lifts, the
      neighbours part a little either side so it can be seen whole. */
  const [hovered, setHovered] = React.useState<number | null>(null)
  const timers = React.useRef<number[]>([])

  const later = React.useCallback((fn: () => void, ms: number) => {
    const t = window.setTimeout(fn, ms)
    timers.current.push(t)
    return t
  }, [])

  React.useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), [])
  React.useEffect(() => wheelAudio.preload(), [])

  const setCard = (i: number, patch: Partial<CardState>) =>
    setCards((prev) => prev.map((c, k) => (k === i ? { ...c, ...patch } : c)))

  const open = () => {
    if (phase !== "idle") return
    cardsAudio.unlock()
    // The wheel whoosh is wanted on Apply; its context needs this gesture.
    wheelAudio.unlock()
    cardsAudio.pack()
    setPhase("opening")

    // Fan out, face down.
    for (let i = 0; i < n; i++) {
      later(() => {
        setCard(i, { spot: "fan" })
        cardsAudio.deal()
      }, OPEN_HOLD_MS + i * DEAL_STAGGER_MS)
    }
    const fanned = OPEN_HOLD_MS + n * DEAL_STAGGER_MS + DEAL_MS

    // The beat: silence, nothing moves; the lamps fill up round the ring
    // toward the top, reaching it at the turn.
    later(() => setLamps("build"), fanned)

    // The turn, a ripple along the fan; the lamps wave out from the top.
    const turn = fanned + TEASE_MS
    later(() => setLamps("wave"), turn)
    for (let i = 0; i < n; i++) {
      later(() => {
        setCard(i, { faceUp: true, walkout: true })
        cardsAudio.flip()
      }, turn + i * FLIP_STAGGER_MS)
    }

    later(() => setPhase("done"), turn + n * FLIP_STAGGER_MS + FLIP_MS + SETTLE_MS)
  }

  /** Stage-space centre of the slip row for a market, or a spot at the
      slip's edge when the row cannot be found. */
  const rowSpot = (marketBaseId: string | undefined, i: number) => {
    const stage = stageRef.current?.getBoundingClientRect()
    if (!stage) return { x: 0, y: 0 }
    const slip = document.querySelector<HTMLElement>("[data-cards-slip]")
    const row = marketBaseId ? slip?.querySelector(`[data-pick-id="${marketBaseId}"]`) : null
    const r = (row ?? null)?.getBoundingClientRect()
    if (r && slip) {
      // The slip may still be sliding in (the board's panel enters with the
      // first pick), so the row is placed where it WILL be: the slip's
      // resting box (right edge, its own top) plus the row's offset in it.
      const a = slip.getBoundingClientRect()
      const restLeft = window.innerWidth - a.width
      const restTop = parseFloat(getComputedStyle(slip).top) || a.top
      const rowLeft = restLeft + (r.left - a.left)
      const rowTop = restTop + (r.top - a.top)
      // Folded rows have no height yet; the row opens to about 100px, and
      // a folded group puts its ~56px header above it first.
      const group = row?.closest("[data-slot='slip-group']") as HTMLElement | null
      const groupFolded = group ? group.getBoundingClientRect().height < 4 : false
      const opens = r.height < 4 ? 100 : r.height
      const headerAbove = groupFolded ? 56 : 0
      return { x: rowLeft + r.width * 0.42 - stage.left, y: rowTop + headerAbove + opens / 2 - stage.top }
    }
    const sr = slip?.getBoundingClientRect()
    return sr
      ? { x: sr.left + 150 - stage.left, y: sr.top + 140 + i * 64 - stage.top }
      : { x: stage.width - 80, y: 120 + i * 64 }
  }

  /** Each card, in turn: lifts off the fan; its pick goes into the slip
      unseen so its row has a place; it swoops onto that place; and at the
      moment of contact the row enters and kicks in, sparks fly, the count
      climbs. */
  const apply = () => {
    if (phase !== "done") return
    setHovered(null)
    setPhase("flying")
    setLamps("chase")
    setLanded(0)
    wheelAudio.unlock()
    wheelAudio.start()
    // The pack IS the lineup: whatever was in the slip goes, and the slip is
    // a promo lineup from here on (sides fixed, no per-row remove, Clear all only).
    clearSlip()
    lockPromoLineup()
    // Each card's side is drawn here, once, not by the player.
    const sides = live?.map((card) =>
      card.outcomeMode === "less-only" ? "less"
      : card.outcomeMode === "more-only" ? "more"
      : Math.random() < 0.5 ? "less" : "more"
    ) as ("less" | "more")[] | undefined
    for (let i = 0; i < n; i++) {
      const t0 = 250 + i * FLY_STAGGER_MS
      later(() => setCard(i, { spot: "lift" }), t0)
      later(() => {
        const card = live?.[i]
        if (card) {
          const side = sides?.[i] ?? "more"
          hidePicks([card.marketBaseId])
          addPick(createSlipPickFromCard(card, side))
        }
        // The row is laid out on the next frame; then the card knows where to go.
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            setTargets((prev) => ({ ...prev, [i]: rowSpot(card?.marketBaseId, i) }))
            setCard(i, { spot: "slip" })
            wheelAudio.swish()
          })
        )
      }, t0 + LIFT_MS)
      later(() => {
        const card = live?.[i]
        if (card) {
          revealPicks([card.marketBaseId])
          flashPicks([card.marketBaseId])
        }
        setTargets((prev) => {
          const at = prev[i]
          if (at) setBursts((b) => [...b, { key: Date.now() + i, x: at.x, y: at.y }])
          return prev
        })
        setLanded(i + 1)
        cardsAudio.land()
      }, t0 + LIFT_MS + 40 + FLY_MS)
    }
    later(() => {
      setPhase("applied")
      setLamps("kick")
      onApplied?.()
    }, 250 + n * FLY_STAGGER_MS + LIFT_MS + FLY_MS + 600)
  }

  const again = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
    setCards(fresh())
    setHovered(null)
    setTargets({})
    setBursts([])
    setLanded(0)
    setLamps("off")
    setPhase("idle")
  }

  /* Geometry of the stage, in px, from a 1180 x 640 box. The hand is a fan
     of full-size cards overlapping, each turned a little more than the
     last about a pivot below the fan, the way a hand is held. */
  const stageW = 1320
  const stageH = 640
  const cardH = CARD_W * 1.4
  const stackX = stageW / 2 - CARD_W / 2
  const stackY = stageH / 2 - cardH / 2 - 30
  const mid = (n - 1) / 2
  const fanY = stageH / 2 - cardH / 2 - 10

  /** Card i's slot in the fan: the top of the stack (card 0) takes the
      right-most slot, each card under it the next slot left, so the deal
      reads as peeling off the top and each new card sliding in under. */
  const slotOf = (i: number) => n - 1 - i

  /** Where card i rests in the fan, before any hover shift. */
  const fanBase = (i: number) => {
    const k = slotOf(i) - mid
    return { x: stageW / 2 - CARD_W / 2 + k * FAN_STEP_PX, y: fanY + k * k * 4, deg: k * FAN_STEP_DEG }
  }

  /** Is the stage point inside card i's resting outline? */
  const inside = (i: number, px: number, py: number) => {
    const { x, y, deg } = fanBase(i)
    // The pivot the card turns about: 50% across, 115% down.
    const ox = x + CARD_W / 2
    const oy = y + cardH * 1.15
    const a = (-deg * Math.PI) / 180
    const dx = px - ox
    const dy = py - oy
    const lx = ox + dx * Math.cos(a) - dy * Math.sin(a) - x
    const ly = oy + dx * Math.sin(a) + dy * Math.cos(a) - y
    return lx >= 0 && lx <= CARD_W && ly >= 0 && ly <= cardH
  }

  /** Which card the pointer is over, tested against the resting fan so the
      answer does not change while the cards move for the hover. The card
      already lifted keeps the hover for as long as the pointer is anywhere
      on its outline (it is on top now, so all of it is visible); otherwise
      the top card (the later one) wins where they overlap. */
  const stageRef = React.useRef<HTMLDivElement>(null)
  const hitTest = (clientX: number, clientY: number, current: number | null): number | null => {
    const box = stageRef.current?.getBoundingClientRect()
    if (!box) return null
    const px = clientX - box.left
    const py = clientY - box.top
    if (current !== null && inside(current, px, py)) return current
    // Lower index is higher in the pile, so it wins where two overlap.
    for (let i = 0; i < n; i++) if (inside(i, px, py)) return i
    return null
  }

  const transformFor = (i: number, s: CardState) => {
    if (s.spot === "lift") {
      // Up off the fan, squared up, ready to go.
      const base = fanBase(i)
      return `translate(${base.x}px, ${base.y - 140}px) rotate(0deg) scale(1)`
    }
    if (s.spot === "slip") {
      // Onto its row. The card scales about its pivot (50% across, 115%
      // down), so the translate is solved for the scaled card's centre to
      // sit on the target.
      const t = targets[i] ?? { x: stageW - 80, y: 120 }
      const x = t.x - CARD_W / 2
      const y = t.y - cardH * 1.15 + 0.65 * FLY_SCALE * cardH
      return `translate(${x}px, ${y}px) rotate(0deg) scale(${FLY_SCALE})`
    }
    if (s.spot === "stack") {
      return `translate(${stackX + i * STACK_OFFSET}px, ${stackY - i * STACK_OFFSET}px) rotate(${(i % 3) - 1}deg)`
    }
    const base = fanBase(i)
    let x = base.x
    let y = base.y
    if (hovered !== null && phase === "done") {
      if (i === hovered) {
        y -= 34
      } else {
        // Part: the two beside it most, the rest a little, each to its side.
        const side = slotOf(i) < slotOf(hovered) ? -1 : 1
        const near = Math.abs(i - hovered) === 1
        x += side * (near ? 52 : 24)
      }
    }
    return `translate(${x}px, ${y}px) rotate(${base.deg}deg)`
  }

  return (
    <div className={cn("relative", className)}>
      {/* The room goes dark round the stage while the pack opens. */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none fixed inset-0 z-[305] opacity-0 motion-reduce:hidden",
          phase === "opening" || phase === "flying" ? "cards-dim-in" : phase === "done" || phase === "applied" ? "cards-dim-out" : ""
        )}
        style={{
          background:
            "radial-gradient(ellipse 50% 44% at 50% 46%, transparent 0%, transparent 62%, hsl(var(--black-0) / 0.7) 84%, hsl(var(--black-0) / 1) 100%)",
        }}
      />

      <div
        ref={stageRef}
        className="relative z-[306] mx-auto cursor-default select-none"
        style={{ width: stageW, height: stageH, perspective: 1600 }}
        onPointerMove={(e) => {
          if (phase === "done") setHovered((h) => hitTest(e.clientX, e.clientY, h))
        }}
        onPointerLeave={() => setHovered(null)}
      >
        {/* The lamps round the hand, under the cards. */}
        <CardLamps
          program={lamps}
          buildMs={TEASE_MS}
          stageW={stageW}
          topY={fanY - 44}
          bottomY={fanY + cardH + 30}
          halfW={560}
          topSag={18}
          bottomSag={40}
        />
        {pack.cards.map((card, i) => {
          const s = cards[i]
          return (
            <div
              key={`${card.name}-${card.statLabel}-${i}`}
              className="absolute left-0 top-0"
              style={{
                width: CARD_W,
                height: cardH,
                transformOrigin: "50% 115%",
                transform: transformFor(i, s),
                opacity: s.spot === "slip" ? 0 : 1,
                // The deal and the hover on one slow, soft curve: in gently, out
                // long. The lift is quick and springy; the swoop starts slow
                // and arrives fast, and the card fades in the last stretch.
                transition:
                  s.spot === "slip"
                    ? `transform ${FLY_MS}ms cubic-bezier(0.55, 0, 0.35, 1), opacity 200ms ease-in ${FLY_MS - 160}ms`
                    : s.spot === "lift"
                      ? `transform ${LIFT_MS}ms cubic-bezier(0.2, 0.9, 0.3, 1.15)`
                      : `transform ${phase === "done" ? 760 : DEAL_MS}ms cubic-bezier(0.3, 0, 0.1, 1)`,
                // The pile order never changes: card 0 on top, whether stacked or fanned.
                zIndex: hovered === i ? 70 : s.spot === "slip" || s.spot === "lift" ? 80 : n - i,
                // A box shadow, not a filter: filters on 3D-transforming
                // elements repaint every frame and shake.
                borderRadius: 18,
                boxShadow: "0 24px 32px hsl(var(--black-0) / 0.6)",
                willChange: "transform",
              }}
            >
              {/* The walkout: sparks thrown out from the card's edges. */}
              {s.walkout && (
                <span aria-hidden className="pointer-events-none absolute inset-0 z-[2] overflow-visible">
                  {Array.from({ length: SPARKS }, (_, k) => {
                    const ang = (k / SPARKS) * Math.PI * 2 + (k % 2) * 0.2
                    const dist = 150 + (k % 3) * 60
                    return (
                      <span
                        key={k}
                        className="cards-spark absolute left-1/2 top-1/2 rounded-full"
                        style={{
                          width: k % 3 === 0 ? 6 : 4,
                          height: k % 3 === 0 ? 6 : 4,
                          background: k % 4 === 0 ? "hsl(335 83% 82%)" : "hsl(var(--white-0))",
                          ["--dx" as string]: `${Math.cos(ang) * dist}px`,
                          ["--dy" as string]: `${Math.sin(ang) * dist}px`,
                          animationDelay: `${(k * 23) % 180}ms`,
                        }}
                      />
                    )
                  })}
                </span>
              )}
              {/* The turn: back and face on one card, rotated on its spine. */}
              <div
                className="relative h-full w-full"
                style={{
                  transformStyle: "preserve-3d",
                  transform: `rotateY(${s.faceUp ? 180 : 0}deg)`,
                  transition: `transform ${FLIP_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              >
                <div className="absolute inset-0" style={{ backfaceVisibility: "hidden" }}>
                  <CardBack width={CARD_W} />
                </div>
                <div className="absolute inset-0" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
                  <CardFace data={card} width={CARD_W} />
                </div>
              </div>
            </div>
          )
        })}

        {/* Sparks where a card has landed on its row. */}
        {bursts.map((b) => (
          <span key={b.key} aria-hidden className="pointer-events-none absolute left-0 top-0 z-[90]" style={{ transform: `translate(${b.x}px, ${b.y}px)` }}>
            {Array.from({ length: LAND_SPARKS }, (_, k) => {
              const ang = (k / LAND_SPARKS) * Math.PI * 2 + (k % 2) * 0.25
              const dist = 70 + (k % 3) * 30
              return (
                <span
                  key={k}
                  className="cards-spark absolute left-0 top-0 rounded-full"
                  style={{
                    width: k % 3 === 0 ? 5 : 3,
                    height: k % 3 === 0 ? 5 : 3,
                    background: k % 4 === 0 ? "hsl(335 83% 82%)" : "hsl(var(--white-0))",
                    ["--dx" as string]: `${Math.cos(ang) * dist}px`,
                    ["--dy" as string]: `${Math.sin(ang) * dist}px`,
                    animationDelay: `${(k * 19) % 120}ms`,
                  }}
                />
              )
            })}
          </span>
        ))}

        {/* The pack line and the button, under the stack while it is closed. */}
        {phase === "idle" && (
          <div
            className="absolute left-1/2 flex -translate-x-1/2 flex-col items-center gap-3"
            style={{ top: stackY + cardH + 28 }}
          >
            <SlideToOpen onOpen={open} />
          </div>
        )}
        {/* The actions come in a beat after the hand has settled, rising
            gently into place. */}
        {phase === "done" && (
          <div
            className="absolute left-1/2 flex -translate-x-1/2 flex-col items-center gap-3 cards-actions-in"
            style={{ top: fanY + cardH + 74 }}
          >
            <div className="flex items-center gap-4">
              <Button type="button" onClick={apply} className="h-11 rounded-full px-6">
                Apply lineup
              </Button>
              <ShareRow pack={pack} />
              <Button type="button" variant="ghost" onClick={again} className="h-11 rounded-full px-5 text-grey-400 hover:text-white">
                Open again
              </Button>
            </div>
          </div>
        )}
        {/* The count and Open again, on /cards only: a host that takes the
            landing (the takeover) has the board to go back to instead. */}
        {phase === "applied" && !onApplied && (
          <div
            className="absolute left-1/2 flex -translate-x-1/2 flex-col items-center gap-3"
            style={{ top: fanY + cardH / 2 - 40 }}
          >
            <p className="font-open-sauce text-[36px] font-black leading-none tracking-[-0.04em] tabular-nums text-white">
              {n} <span className="text-white/40">picks</span>
            </p>
            <p className="font-open-sauce text-[11px] font-semibold tracking-tight text-white/55">in your slip</p>
            <Button type="button" variant="ghost" onClick={again} className="mt-2 h-11 rounded-full px-5 text-grey-400 hover:text-white">
              Open again
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
